import React, { useEffect, useState, useRef } from "react";
import { getGeocodingStatus, startGeocoding, stopGeocoding, geocodeSingle, updateProduttore } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Input } from "../components/ui/input";
import { Play, Square, RefreshCw, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Geocoding() {
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const intervalRef = useRef(null);

  const load = async () => {
    try {
      const s = await getGeocodingStatus();
      setStatus(s);
      return s;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    load();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  useEffect(() => {
    if (status?.running && !polling) {
      setPolling(true);
      intervalRef.current = setInterval(async () => {
        const s = await load();
        if (!s?.running) {
          clearInterval(intervalRef.current);
          setPolling(false);
        }
      }, 2000);
    }
  }, [status?.running, polling]);

  const start = async () => {
    try {
      const r = await startGeocoding();
      toast.success(`Geocoding avviato (${r.to_process})`);
      load();
    } catch (e) {
      toast.error("Errore avvio");
    }
  };

  const stop = async () => {
    await stopGeocoding();
    toast.info("Geocoding fermato");
    load();
  };

  const retrySingle = async (pid) => {
    try {
      const r = await geocodeSingle(pid);
      if (r.lat != null) {
        toast.success("Trovato");
      } else {
        toast.error(r.error || "Nessun risultato");
      }
      load();
    } catch {
      toast.error("Errore");
    }
  };

  const saveManual = async (pid, lat, lng) => {
    try {
      await updateProduttore(pid, { latitudine: Number(lat), longitudine: Number(lng) });
      toast.success("Coordinate salvate");
      load();
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  if (!status) return <div className="p-4 text-slate-500">Caricamento…</div>;

  const pct = status.progress_total > 0 ? (status.progress_done / status.progress_total) * 100 : 0;

  return (
    <div className="space-y-4" data-testid="geocoding-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Geocoding</h1>
        <p className="text-slate-500 text-sm mt-1">Nominatim · max 1 richiesta/sec</p>
      </div>

      <Card className="p-5 bg-white border border-slate-200 shadow-sm">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-sm text-slate-500">Stato</div>
          <div className="text-lg font-bold text-slate-900" data-testid="geocoding-status">
            {status.geocoded} / {status.total} geocodificati
          </div>
        </div>
        <Progress value={status.total ? (status.geocoded / status.total) * 100 : 0} className="h-2" />

        {status.running && (
          <div className="mt-4 p-3 bg-[#F57C00]/10 rounded-lg border border-[#F57C00]/30">
            <div className="flex items-center gap-2 text-sm text-[#F57C00] font-semibold">
              <Loader2 size={16} className="animate-spin" /> In corso: {status.progress_done} / {status.progress_total}
            </div>
            {status.current && (
              <div className="text-xs text-slate-600 mt-1 truncate">
                {status.current.nome} — {status.current.indirizzo}
              </div>
            )}
            <Progress value={pct} className="h-2 mt-2" />
          </div>
        )}

        <div className="flex gap-2 mt-4">
          {!status.running ? (
            <Button
              disabled={status.pending === 0}
              onClick={start}
              className="bg-[#1B5E20] hover:bg-[#144818] text-white min-h-[44px]"
              data-testid="start-geocoding-btn"
            >
              <Play size={16} className="mr-2" /> Avvia geocodifica ({status.pending} rimasti)
            </Button>
          ) : (
            <Button variant="outline" onClick={stop} className="min-h-[44px]" data-testid="stop-geocoding-btn">
              <Square size={16} className="mr-2" /> Interrompi
            </Button>
          )}
          <Button variant="outline" onClick={load} className="min-h-[44px]"><RefreshCw size={16} /></Button>
        </div>
      </Card>

      {status.failed && status.failed.length > 0 && (
        <Card className="p-4 bg-white border border-slate-200 shadow-sm" data-testid="failed-list">
          <div className="text-sm font-semibold text-[#C62828] mb-2">Falliti ({status.failed.length})</div>
          <ul className="divide-y divide-slate-100">
            {status.failed.map((f) => (
              <FailedRow key={f.id} f={f} onRetry={retrySingle} onManual={saveManual} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function FailedRow({ f, onRetry, onManual }) {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 truncate">{f.ragioneSociale}</div>
          <div className="text-xs text-slate-500 truncate">{f.indirizzo}</div>
          <div className="text-xs text-[#C62828] mt-1">{f.error}</div>
        </div>
        <Button size="sm" variant="outline" onClick={() => onRetry(f.id)} data-testid={`retry-${f.id}`} className="min-h-[40px]">
          <MapPin size={14} className="mr-1" /> Riprova
        </Button>
      </div>
      <div className="flex gap-2 mt-2">
        <Input placeholder="Lat" value={lat} onChange={(e) => setLat(e.target.value)} className="h-9" />
        <Input placeholder="Lng" value={lng} onChange={(e) => setLng(e.target.value)} className="h-9" />
        <Button size="sm" onClick={() => onManual(f.id, lat, lng)} disabled={!lat || !lng} className="bg-[#F57C00] hover:bg-[#E65100] text-white min-h-[40px]" data-testid={`manual-${f.id}`}>
          Salva
        </Button>
      </div>
    </li>
  );
}
